import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  useTheme,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/InfoContext";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import ConfirmationDialog from "../VersatileComponents/ConfirmationDialog";
import {
  getAccessoryTypes,
  deleteAccessoryType,
} from "../../api/accessories_given_to_workers/accessoryTypes";
import AccessoryTypeForm from "./AccessoryTypeForm";
import useUserClaims from "../../hooks/useUserClaims";

const AccessoryTypeTable = () => {
  const params = useParams();
  const [data, setData] = useState([]);
  const { user } = useAuth();
  const userClaim = useUserClaims(user);
  const [editRow, setEditRow] = useState(null);
  const { setIsSubmitting } = useContext(SpinnerContext);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { openSnackbar } = useSnackbar();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const theme = useTheme();

  const handleEdit = (row) => {
    setEditRow(row);
    setIsEditDialogOpen(true);
  };


  const handleDelete = (id) => {
    openDeleteConfirmationDialog(id);
  };

  const handleDeleteConfirmed = async () => {
    setIsSubmitting(true);
    closeDeleteConfirmationDialog();
    try {
      const res = await deleteAccessoryType(user, deleteItemId);
      openSnackbar(
        res.data.message || "Accessory type deleted successfully.",
        "success"
      );
    } catch (error) {
      openSnackbar(
        error.message ||
          "An unexpected error occurred. Please check your connection.",
        "error"
      );
    }
    setIsSubmitting(false);
    setDeleteItemId(null);
  };

  const openDeleteConfirmationDialog = (id) => {
    setDeleteItemId(id);
    setIsDeleteDialogOpen(true);
  };

  const closeDeleteConfirmationDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const loadInitialData = async () => {
    try {
      console.log("AccessoryTypeTable - params.id:", params.id);
      getAccessoryTypes(params.id, setData);
    } catch (error) {
      console.error("Error loading accessory types:", error);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [params.id]);

  return (
    <Box m="1rem 0">
      <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
        Manage Accessory Types
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.palette.background.alt }}>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {data.map((accessoryType) => (
            <Grid item xs={12} sm={3} key={accessoryType.id}>
              <Card
                sx={{
                  backgroundColor: theme.palette.background.alt,
                  border: `1px solid ${
                    theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                  }`,
                  "&:hover": {
                    boxShadow: 2,
                  },
                }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.secondary[100],
                      fontWeight: 500,
                    }}
                  >
                    {accessoryType.type}
                  </Typography>

                  {(userClaim.admin || userClaim.subAdmin) && (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(accessoryType)}
                        sx={{
                          color: theme.palette.secondary[100],
                          "&:hover": {
                            backgroundColor: theme.palette.background.default,
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(accessoryType.id)}
                        sx={{
                          color: theme.palette.error.main,
                          "&:hover": {
                            backgroundColor: theme.palette.background.default,
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Create form at the bottom */}
        <Box mt={3}>
          <AccessoryTypeForm />
        </Box>
      </DialogContent>

      <ConfirmationDialog
        open={isDeleteDialogOpen}
        handleDialogClose={closeDeleteConfirmationDialog}
        handleConfirmed={handleDeleteConfirmed}
        message="Are you sure you want to delete this accessory type?"
        title="Delete Confirmation"
      />

      {isEditDialogOpen && (
        <AccessoryTypeForm
          editData={editRow}
          isEditDialogOpen={isEditDialogOpen}
          closeEditDialog={() => setIsEditDialogOpen(false)}
        />
      )}
    </Box>
  );
};

export default AccessoryTypeTable;
